<script setup lang="ts">
import { computed, ref } from 'vue'
import { ArrowUpRight, Anchor } from 'lucide-vue-next'
import type { AppLocale } from '@/i18n/messages'
import type { SceneSkin } from '@/ui/pip-skins'
import type { StreamHudData } from '@/ui/stream-hud'
import { isIdleMotionActive, useAdventureMotion } from '@/ui/adventure-motion'
import { sceneMotion } from '@/ui/scene-motion'
import AdventureMetric from './AdventureMetric.vue'
import SceneProgress from './SceneProgress.vue'
import StreamCompanion from './StreamCompanion.vue'

const props = defineProps<{ skin: SceneSkin; data: StreamHudData; locale: AppLocale; preview?: boolean }>()
const root = ref<HTMLElement>()
const motionActive = computed(() => isIdleMotionActive(props.data.status))
const ratio = computed(() => Number.isFinite(props.data.expRatio) ? Math.max(0, Math.min(1, props.data.expRatio)) : 0)
// StreamHud keys this component by skin so each theme owns and cleans up its effects.
const feedback = useAdventureMotion(root, () => ({ data: props.data, skin: props.skin, preview: props.preview }), sceneMotion[props.skin])
</script>

<template>
  <div ref="root" class="adventure-composition scene-composition" :data-feedback="feedback" :data-motion-active="motionActive" :data-preview="preview" data-stream-measure>
    <div v-if="skin === 'stream-kerning'" class="kerning-ticket" data-stream-measure>
      <section class="kerning-main" data-stream-measure>
        <div class="kerning-masthead" data-tier="1" data-stream-measure>
          <span class="kerning-station adventure-level" data-stream-measure>Lv. <b>{{ data.level }}</b></span>
          <span class="kerning-warning" data-tier="5" aria-hidden="true" />
          <span class="kerning-arrow-feedback" data-tier="3" aria-hidden="true"><ArrowUpRight /></span>
        </div>
        <AdventureMetric kind="exp" :data="data" :locale="locale" />
        <SceneProgress :skin="skin" :data="data" :locale="locale" />
        <div class="kerning-sticker-rail" data-tier="3" aria-hidden="true">
          <span class="kerning-dash-track"><i class="scene-idle" /></span>
          <StreamCompanion name="octopus" class="kerning-octopus" :active="motionActive" :preview="preview" />
          <svg class="kerning-speed-lines" viewBox="0 0 54 20"><path d="M2 4h36M10 10h42M2 16h28" /></svg>
        </div>
      </section>
      <aside v-if="data.enabled.mesos" class="kerning-stub" data-tier="2" data-stream-measure>
        <AdventureMetric kind="mesos" :data="data" :locale="locale" />
        <span class="kerning-barcode" data-tier="5" aria-hidden="true" />
      </aside>
    </div>

    <div v-else class="aqua-voyage" data-stream-measure>
      <section class="aqua-readout" data-stream-measure>
        <div class="aqua-heading" data-tier="1" data-stream-measure><span class="adventure-level" data-stream-measure>Lv. <b>{{ data.level }}</b></span><Anchor aria-hidden="true" /></div>
        <AdventureMetric kind="exp" :data="data" :locale="locale" />
        <SceneProgress :skin="skin" :data="data" :locale="locale" />
      </section>
      <div class="aqua-porthole" data-tier="3" aria-hidden="true">
        <div class="aqua-window">
          <svg class="aqua-reef" viewBox="0 0 100 36"><path d="M0 36v-8l12-7 9 9 12-5 14 6 15-9 15 5 23-12v21Z" fill="#365b59" /><path d="M12 34V15m0 10-6-6m6 2 7-9m60 22V10m0 14-8-8m8 4 7-6" fill="none" stroke="#c38d73" stroke-width="3" /></svg>
          <div class="aqua-drift scene-idle"><StreamCompanion name="cico" class="aqua-cico" :active="motionActive" :preview="preview" /></div>
          <span class="aqua-bubbles"><i class="scene-idle" /><i class="scene-idle" /><i class="scene-idle" /></span>
          <span class="aqua-celebration"><i v-for="particle in 3" :key="particle" data-feedback-particle /></span>
        </div>
        <svg class="aqua-depth-arc" viewBox="0 0 110 150"><path class="aqua-arc-base" d="M18 6C130 6 130 144 18 144" /><path class="aqua-arc-fill" d="M18 6C130 6 130 144 18 144" pathLength="100" stroke-dasharray="100" :stroke-dashoffset="100 * (1 - ratio)" /><path class="aqua-arc-ticks" d="M18 1v10m81 61h10M18 139v10" /></svg>
      </div>
      <aside v-if="data.enabled.mesos" class="aqua-salvage" data-tier="2" data-stream-measure><AdventureMetric kind="mesos" :data="data" :locale="locale" /></aside>
    </div>
  </div>
</template>
